import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { GlobalConstant } from '@shared/global.constant';
import { ModelEnergy } from '@shared/models/business/model-energy.model';
import { EnergyUsage } from '../../_models/vehicleInfo/energyUsage/energy-usage.model';
import { VehicleConfig } from '../../_models/vehicleInfo/vehicleConfig/vehicle-config.model';
import { VehicleUsage } from '../../_models/vehicleInfo/vehicleUsage/vehicle-usage.model';

@Injectable({
	providedIn: 'root',
})
export class VehicleInformationFormService {
	public energyUsageForm: FormGroup;
	public vehicleConfigForm: FormGroup;
	public vehicleUsageForm: FormGroup;
	public models: ModelEnergy[];

	constructor(private formBuild: FormBuilder) {}

	/** Init Vehicule-Usage Form Reactive */
	public initVehicleUsageForm(vehicleUsage: VehicleUsage) {
		this.vehicleUsageForm = this.formBuild.group({
			application: [vehicleUsage.application, [Validators.required]],
			bodyType: [vehicleUsage.bodyType ? vehicleUsage.bodyType : null],
			usageType: [vehicleUsage.usageType ? vehicleUsage.usageType : null],
			operatingDaysPerYear: [vehicleUsage.operatingDaysPerYear, [Validators.required, Validators.min(1)]],
			shiftPerDay: [vehicleUsage.shiftPerDay, [Validators.required, Validators.min(1)]],
			dailyMileage: [vehicleUsage.dailyMileage, [Validators.required, Validators.min(1)]],
			yearlyMileage: [vehicleUsage.yearlyMileage, [Validators.required, Validators.min(1)]],
			contractYear: [vehicleUsage.contractYear, [Validators.required, Validators.min(1)]],
			contractMonth: [vehicleUsage.contractMonth, [Validators.required, Validators.min(1)]],
		});
	}

	/** Init Vehicle-Config Form Reactive */
	public initVehicleConfigForm(vehicleConfig: VehicleConfig, comparisonId: number) {
		switch (comparisonId) {
			case GlobalConstant.COMPTYPE_ED:
				vehicleConfig.diesel.model.name = vehicleConfig.diesel.model.code;
				this.vehicleConfigForm = this.formBuild.group({
					electric: this.formBuild.group({
						battery: [vehicleConfig.electric.battery, [Validators.required]],
						model: [vehicleConfig.electric.model ? vehicleConfig.electric.model : new ModelEnergy(), [Validators.required]],
						payload: [vehicleConfig.electric.payload, [Validators.required, Validators.min(0)]],
					}),
					diesel: this.formBuild.group({
						energySubtype: [
							vehicleConfig.diesel.energySubtype ? vehicleConfig.diesel.energySubtype : null,
							[Validators.required],
						],
						model: [vehicleConfig.diesel.model ? vehicleConfig.diesel.model?.name : '', [Validators.required]],
						payload: [vehicleConfig.diesel.payload, [Validators.required, Validators.min(0)]],
					}),
				});
				break;
			case GlobalConstant.COMPTYPE_EDG:
				vehicleConfig.diesel.model.name = vehicleConfig.diesel.model.code;
				vehicleConfig.gas.model.name = vehicleConfig.gas.model.code;
				this.vehicleConfigForm = this.formBuild.group({
					electric: this.formBuild.group({
						battery: [vehicleConfig.electric.battery, [Validators.required]],
						model: [vehicleConfig.electric.model ? vehicleConfig.electric.model : new ModelEnergy(), [Validators.required]],
						payload: [vehicleConfig.electric.payload, [Validators.required, Validators.min(0)]],
					}),
					diesel: this.formBuild.group({
						energySubtype: [
							vehicleConfig.diesel.energySubtype ? vehicleConfig.diesel.energySubtype : null,
							[Validators.required],
						],
						model: [vehicleConfig.diesel.model ? vehicleConfig.diesel.model?.name : '', [Validators.required]],
						payload: [vehicleConfig.diesel.payload, [Validators.required, Validators.min(0)]],
					}),
					gas: this.formBuild.group({
						model: [vehicleConfig.gas.model ? vehicleConfig.gas.model?.name : '', [Validators.required]],
						payload: [vehicleConfig.gas.payload, [Validators.required, Validators.min(0)]],
					}),
				});
				break;
			case GlobalConstant.COMPTYPE_EG:
				vehicleConfig.gas.model.name = vehicleConfig.gas.model.code;
				this.vehicleConfigForm = this.formBuild.group({
					electric: this.formBuild.group({
						battery: [vehicleConfig.electric.battery, [Validators.required]],
						model: [vehicleConfig.electric.model ? vehicleConfig.electric.model : null, [Validators.required]],
						payload: [vehicleConfig.electric.payload, [Validators.required, Validators.min(0)]],
					}),
					gas: this.formBuild.group({
						model: [vehicleConfig.gas.model ? vehicleConfig.gas.model?.name : '', [Validators.required]],
						payload: [vehicleConfig.gas.payload, [Validators.required, Validators.min(0)]],
					}),
				});
				break;
			case GlobalConstant.COMPTYPE_E:
				this.vehicleConfigForm = this.formBuild.group({
					electric: this.formBuild.group({
						battery: [vehicleConfig.electric.battery, [Validators.required]],
						model: [vehicleConfig.electric.model ? vehicleConfig.electric.model : null, [Validators.required]],
						payload: [vehicleConfig.electric.payload, [Validators.required, Validators.min(0)]],
					}),
				});
				break;
			case GlobalConstant.COMPTYPE_EE:
				vehicleConfig.electricComp.model.name = vehicleConfig.electricComp.model.code;
				this.vehicleConfigForm = this.formBuild.group({
					electric: this.formBuild.group({
						battery: [vehicleConfig.electric.battery, [Validators.required]],
						model: [vehicleConfig.electric.model ? vehicleConfig.electric.model : null, [Validators.required]],
						payload: [vehicleConfig.electric.payload, [Validators.required, Validators.min(0)]],
					}),
					electricComp: this.formBuild.group({
						battery: [vehicleConfig.electricComp.battery ? vehicleConfig.electricComp.battery.code : '', [Validators.required]],
						model: [vehicleConfig.electricComp.model ? vehicleConfig.electricComp.model?.name : '', [Validators.required]],
						payload: [vehicleConfig.electricComp.payload, [Validators.required, Validators.min(0)]],
					}),
				});
				break;
		}
	}

	/** Init Energy-Usage Form Reactive */
	public initEnergyUsageForm(energyUsage: EnergyUsage, comparisonId: number) {
		const elecUsageForm = this.formBuild.group({
			consumption: [energyUsage.electric.consumption ?? 0, [Validators.required, this.isNaNValidator(), Validators.min(0.001)]],
		});
		switch (comparisonId) {
			case GlobalConstant.COMPTYPE_ED:
				const dieselUsageForm = this.formBuild.group({
					adblue: [energyUsage.diesel.adblue, [Validators.required, this.isNaNValidator(), Validators.min(0.001)]],
					consumption: [energyUsage.diesel.consumption ?? 0, [Validators.required, this.isNaNValidator(), Validators.min(0.001)]],
				});

				this.energyUsageForm = this.formBuild.group({
					electric: elecUsageForm,
					diesel: dieselUsageForm,
				});
				break;
			case GlobalConstant.COMPTYPE_EDG:
				const dieselUsageForm2 = this.formBuild.group({
					adblue: [energyUsage.diesel.adblue, [Validators.required, this.isNaNValidator(), Validators.min(0.001)]],
					consumption: [energyUsage.diesel.consumption ?? 0, [Validators.required, this.isNaNValidator(), Validators.min(0.001)]],
				});

				const gasUsageForm2 = this.formBuild.group({
					consumption: [energyUsage.gas.consumption ?? 0, [Validators.required, this.isNaNValidator(), Validators.min(0.001)]],
				});

				this.energyUsageForm = this.formBuild.group({
					electric: elecUsageForm,
					diesel: dieselUsageForm2,
					gas: gasUsageForm2,
				});
				break;
			case GlobalConstant.COMPTYPE_EG:
				const gasUsageForm = this.formBuild.group({
					consumption: [energyUsage.gas.consumption ?? 0, [Validators.required, this.isNaNValidator(), Validators.min(0.001)]],
				});

				this.energyUsageForm = this.formBuild.group({
					electric: elecUsageForm,
					gas: gasUsageForm,
				});
				break;
			case GlobalConstant.COMPTYPE_E:
				this.energyUsageForm = this.formBuild.group({
					electric: elecUsageForm,
				});
				break;
			case GlobalConstant.COMPTYPE_EE:
				const electricCompUsageForm = this.formBuild.group({
					consumption: [
						energyUsage.electricComp.consumption ?? 0,
						[Validators.required, this.isNaNValidator(), Validators.min(0.001)],
					],
				});

				this.energyUsageForm = this.formBuild.group({
					electric: elecUsageForm,
					electricComp: electricCompUsageForm,
				});
				break;
		}
	}

	private isNaNValidator(): ValidatorFn {
		return (control: AbstractControl) => {
			const isValid = !isNaN(control.value) && control.value !== null;
			if (isValid) {
				return null;
			}
			return { IsNaN: true };
		};
	}
}
